import React, { useState } from 'react';
import { Button, Modal, Message } from 'semantic-ui-react';
import { useIntl } from 'react-intl';
import { store } from '../../../store';
import additionalMessages from '../../../messages';
import { getWithAuth, postFileWithAuth } from '../../../api';
import { useSelector } from 'react-redux';
import { uiSet } from '../../mainframe/mainframe.reducer';

import { RootStateOrAny } from 'src/store';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { getLayersQueryKey } from './gisReducer';
import { getCanvas } from 'src/utils/mapUtils';

const DEFAULT_IMPORT_STATE = {
  busy: false,
  importFile: null,
  message: null,
  error: false,
  complete: false,
};

interface ImportGisModalProps {
  onClose?: (filename: string | null) => void;
}

const showLayersForFile = (projectId: string, filename: string) => {
  // Fetch all layers for a particular file at once
  getWithAuth(
    `project/${projectId}/gis_layers/geometry?filename=${filename}`
  ).then((layers) => {
    const gis = getCanvas().gis;
    layers.forEach((layer) => {
      gis.showLayer(layer);
    });
    gis.fitLayers(layers);
  });
};

const ImportGisModal = ({ onClose }: ImportGisModalProps) => {
  const onOpen = () => {
    setImportState(DEFAULT_IMPORT_STATE);
  };

  const onImportFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) {
      return;
    }

    const body = new FormData();
    body.append('file', file);
    body.append('filename', file.name);
    setImportState({
      ...importState,
      importFile: body,
      message: null,
      complete: false,
    });
  };
  const { formatMessage } = useIntl();
  const importRef = React.createRef();

  const [importState, setImportState] = useState(DEFAULT_IMPORT_STATE);
  const { busy } = importState;
  const show = useSelector(
    (state: RootStateOrAny) => state.mainFrame.showGisImportDialog
  );

  const { projectId } = useSelector((state: RootStateOrAny) => state.mainFrame);

  const qc = useQueryClient();
  const doImportGis = () => {
    if (importState.importFile) {
      setImportState({ ...importState, busy: true });
      postFileWithAuth(
        `project/${projectId}/gis_layers`,
        importState.importFile
      )
        .then((res) => {
          // Leave busy running until the task is complete
          if (res.warnings) {
            toast(<Message>{res.warnings}</Message>, {
              autoClose: 2000,
            });
          }
          setImportState({
            ...importState,
            importFile: importState.importFile,
            busy: false,
            error: false,
            message: null,
            complete: true,
          });
          closePopup();
          showLayersForFile(projectId, res.filename);
        })
        .catch((e) => {
          const errorMsg =
            e === 'Invalid file'
              ? formatMessage(additionalMessages.invalidFileError)
              : formatMessage(additionalMessages.fileError);
          setImportState({
            ...importState,
            busy: false,
            error: true,
            message: errorMsg,
            complete: true,
          });
        });
    }
  };
  const closePopup = () => {
    setImportState(DEFAULT_IMPORT_STATE);
    qc.invalidateQueries({
      queryKey: getLayersQueryKey(projectId),
    });
    store.dispatch(uiSet({ showGisImportDialog: false }));
  };
  const okButton = busy ? (
    <Button icon="sync" color="blue" loading />
  ) : (
    <Button
      onClick={() => (importState.complete ? closePopup() : doImportGis())}
      color="blue"
      disabled={!importState.importFile || busy}
    >
      {importState.complete
        ? formatMessage(additionalMessages.close)
        : formatMessage(additionalMessages.ok)}
    </Button>
  );

  return (
    <Modal
      open={show}
      onClose={() => closePopup()}
      onOpen={() => onOpen()}
      closeOnDimmerClick={busy ? false : true}
    >
      <Modal.Header>
        {formatMessage(additionalMessages.importGisFile)}
        <Button
          circular
          icon="close"
          title="Close"
          floated="right"
          onClick={() => closePopup()}
        />
      </Modal.Header>
      <Modal.Content>
        <Button
          content={formatMessage(additionalMessages.selectGISFile)}
          labelPosition="left"
          icon={{ className: 'linkplanner-icon-coloured' }}
          disabled={busy}
          onClick={() => importRef.current.click()}
        />
        <input
          ref={importRef}
          type="file"
          accept=".kml, .kmz"
          hidden
          onChange={onImportFileChange}
        />
        {importState.importFile && importState.importFile.get('filename')}

        {importState.message ? (
          <Message
            error={importState.error}
            onDismiss={
              busy
                ? null
                : () => {
                    setImportState({ ...importState, message: null });
                  }
            }
            header={
              importState.error
                ? formatMessage(additionalMessages.importError)
                : formatMessage(additionalMessages.importGisFile)
            }
            content={importState.message}
          />
        ) : null}
      </Modal.Content>
      <Modal.Actions>
        {busy || importState.complete ? null : (
          <Button onClick={() => closePopup()}>
            {formatMessage(additionalMessages.cancel)}
          </Button>
        )}
        {okButton}
      </Modal.Actions>
    </Modal>
  );
};

export default ImportGisModal;
